import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { useIsAdmin } from '../lib/useIsAdmin';
import { useT } from '../i18n/LangProvider';

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const { isAdmin } = useIsAdmin();
  const { t, lang, toggle } = useT();
  const [signedIn, setSignedIn] = useState<boolean | null>(null);
  useEffect(() => {
    fetch('/.auth/me')
      .then((r) => r.json())
      .then((d) => setSignedIn(!!d?.clientPrincipal))
      .catch(() => setSignedIn(false));
  }, []);

  const PUBLIC_NAV = [
    { to: '/', label: t('nav.home') },
    { to: '/agenda', label: t('nav.agenda') },
    { to: '/speakers', label: t('nav.speakers') },
    { to: '/faq', label: t('nav.faq') }
  ];
  const NAV = isAdmin ? [...PUBLIC_NAV, { to: '/admin', label: t('nav.admin') }] : PUBLIC_NAV;

  const LangToggle = () => (
    <button onClick={toggle} className="glass-pill hover:bg-white/[0.08] transition-colors" aria-label="Toggle language">
      <span className={lang === 'en' ? 'text-ink font-medium' : 'text-ink-muted'}>EN</span>
      <span className="mx-1.5 text-ink-faint">|</span>
      <span className={lang === 'fr' ? 'text-ink font-medium' : 'text-ink-muted'}>FR</span>
    </button>
  );

  return (
    <>
      {/* Fixed logo, floating in top-left corner, overflows the bar */}
      <Link to="/" className="fixed top-3 left-4 md:top-4 md:left-6 z-[60] flex items-center pointer-events-auto">
        <motion.img
          initial={{ opacity: 0, x: -8 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6 }}
          whileHover={{ scale: 1.04 }}
          src="/tecsys-connect-logo-white.png"
          alt="Tecsys Connect 2026"
          className="h-28 md:h-36 w-auto drop-shadow-lg"
        />
      </Link>

      {/* Thin nav bar — height driven ONLY by this h-14 row */}
      <nav className="fixed top-0 left-0 w-full z-50 h-14 flex items-center bg-bg-base/95 backdrop-blur-[3px] border-b border-white/[0.04]">
        <div className="w-full flex items-center justify-end gap-6 md:gap-8 max-w-7xl mx-auto px-6 md:px-10">
          <div className="hidden md:flex gap-7">
            {NAV.map((n) => (
              <Link key={n.to} to={n.to} className={`text-sm lowercase transition-colors relative ${(n.to === '/' ? pathname === '/' : pathname.startsWith(n.to)) ? 'text-ink' : 'text-ink-muted hover:text-ink'}`}>
                {n.label}
                {(n.to === '/' ? pathname === '/' : pathname.startsWith(n.to)) && <motion.span layoutId="navdot" className="absolute -bottom-1.5 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-brand-red" />}
              </Link>
            ))}
          </div>

          <div className="flex items-center gap-3 md:gap-4">
            <LangToggle />
            {signedIn === false
              ? <a href="/.auth/login/aad?post_login_redirect_uri=/" className="hidden md:inline text-sm text-ink-muted hover:text-ink lowercase">{lang === 'fr' ? 'connexion' : 'sign in'}</a>
              : <a href="/.auth/logout?post_logout_redirect_uri=/" className="hidden md:inline text-sm text-ink-muted hover:text-ink lowercase">{t('nav.signOut')}</a>}
            <Link to="/register" className="hidden md:inline-flex items-center gap-1.5 bg-brand-red text-white text-sm rounded-md px-4 py-2 hover:bg-brand-red-soft transition-colors">{t('nav.register')} →</Link>
            <button onClick={() => setOpen((v) => !v)} className="md:hidden w-9 h-9 flex flex-col items-center justify-center gap-1.5">
              <motion.span animate={open ? { rotate: 45, y: 4 } : { rotate: 0, y: 0 }} className="block w-5 h-px bg-ink" />
              <motion.span animate={open ? { rotate: -45, y: -4 } : { rotate: 0, y: 0 }} className="block w-5 h-px bg-ink" />
            </button>
          </div>
        </div>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="md:hidden fixed top-14 left-0 w-full z-40 overflow-hidden bg-bg-elev/95 backdrop-blur-md border-t border-white/5">
            <div className="px-6 py-5 flex flex-col gap-3 max-w-7xl mx-auto">
              {NAV.map((n) => <Link key={n.to} to={n.to} onClick={() => setOpen(false)} className="text-base text-ink lowercase">{n.label}</Link>)}
              <Link to="/register" onClick={() => setOpen(false)} className="bg-brand-red text-white text-sm rounded-md px-4 py-2 self-start mt-2">{t('nav.register')} →</Link>
              <div className="pt-3 mt-2 border-t border-white/[0.06]"><LangToggle /></div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
